import { Component, computed, effect, signal, ViewChild } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteTrigger, MatAutocompleteOrigin, MatAutocomplete } from "@angular/material/autocomplete";
import { Router } from "@angular/router";
import { IAllSearch, IBranchLocation } from "../../interfaces";
import DataService from "../services/data.service";
import ExceptionService from "../services/exception.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from "@angular/material/form-field";
import { MatSelect, MatOption } from "@angular/material/select";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatIconButton } from "@angular/material/button";
import { LoadResultComponent } from "./load-result/load-result.component";
import { OrderResultComponent } from "./order-result/order-result.component";
import { CustomerResultComponent } from "./customer-result/customer-result.component";
import { CarrierResultComponent } from "./carrier-result/carrier-result.component";
import { LocationResultComponent } from "./location-result/location-result.component";
import { MatDivider } from "@angular/material/list";
import { MatTooltip } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { Constants } from "../../../constants";
import { HideForRolesDirective } from "../directives/hideForRoles.directive";

@Component({
    selector: "universal-search",
    templateUrl: "./universal-search.component.html",
    styleUrls: ["./universal-search.component.scss"],
    imports: [
        MatAutocompleteOrigin,
        MatFormField,
        MatSelect,
        FormsModule,
        ReactiveFormsModule,
        MatOption,
        MatLabel,
        MatIcon,
        MatInput,
        MatAutocompleteTrigger,
        MatIconButton,
        MatPrefix,
        MatSuffix,
        MatAutocomplete,
        LoadResultComponent,
        OrderResultComponent,
        CustomerResultComponent,
        CarrierResultComponent,
        LocationResultComponent,
        MatDivider,
        MatTooltip,
        HideForRolesDirective,
    ],
})
export class UniversalSearchComponent {
    keywordFilter: FormControl = new FormControl<string>("");
    keywordList = [
        {
            term: "load",
            displayText: "Load",
            tooltip:
                "Searches by: Load Number, Customer Id, Customer Name, Carrier Id, Carrier Name, PO Numbers, PU Numbers, Order Ref, & Stop Ref",
            denyRoles: [Constants.roles.Customer],
        },
        { term: "ref", displayText: "Ref No.", tooltip: "Searches by: PO, Order Ref, & Stop Ref" },
        {
            term: "order",
            displayText: "Order",
            tooltip: "Searches by: Id, Load Number, Customer Id, Customer Name, Order Ref, & Stop Ref",
        },
        {
            term: "customer",
            displayText: "Customer",
            tooltip: "Searches by: Id, Name, & Address",
            denyRoles: [Constants.roles.Customer],
        },
        {
            term: "carrier",
            displayText: "Carrier",
            tooltip: "Searches by: Id, Name, Address, MC, & DOT",
            denyRoles: [Constants.roles.Customer],
        },
        { term: "location", displayText: "Location", tooltip: "Searches by: Id, Name, Address" },
    ];
    searchText: FormControl = new FormControl<string>("");
    readonly filteredSearchItems = signal<IAllSearch[]>([]);
    readonly hasSearched = signal(false);
    selectedSearchItem: IAllSearch;
    private debounceHandle: any;
    private searchRequest: Subscription | undefined;

    readonly searchTextSignal = toSignal(this.searchText.valueChanges, { initialValue: this.searchText.value });
    readonly selectedKeyword = toSignal(this.keywordFilter.valueChanges, { initialValue: this.keywordFilter.value });
    readonly searchQuery = computed(() => {
        const text = this.searchTextSignal().trim();
        const keyword = this.selectedKeyword();

        if (!text) return null;

        return keyword ? `${keyword} ${text}` : text;
    });

    @ViewChild(MatAutocompleteTrigger) autoSearch: MatAutocompleteTrigger;

    constructor(
        private dialog: MatDialog,
        private dataService: DataService,
        private exceptionService: ExceptionService,
        public router: Router
    ) {
        effect(() => {
            const query = this.searchQuery();

            clearTimeout(this.debounceHandle);

            if (this.searchRequest) {
                this.searchRequest.unsubscribe();
                this.searchRequest = undefined;
            }

            if (!query) {
                this.hasSearched.set(false);
                this.filteredSearchItems.set([]);
                return;
            }

            this.debounceHandle = setTimeout(() => {
                this.universalSearch(query);
            }, 500);
        });
    }

    universalSearch(query: string) {
        this.searchRequest = this.dataService
            .getApiObs<IAllSearch[]>(`Search/All?term=${encodeURIComponent(query)}`)
            .subscribe({
                next: (results) => {
                    this.filteredSearchItems.set(results);
                    this.hasSearched.set(true);
                },
                error: (err) => this.exceptionService.handleException(err),
            });
    }

    onUniversalSearchChange() {
        if (this.searchText.value.DocType !== undefined) {
            switch (this.searchText.value?.DocType) {
                case "load":
                    this.router.navigate(["/dispatch/load/maintenance/" + this.searchText.value.ID]);
                    break;
                case "reference_number":
                case "order":
                    this.router.navigate(["/dispatch/order/maintenance/" + this.searchText.value.ID]);
                    break;
                case "carrier":
                    this.carrierInformation(this.searchText.value.ID);
                    break;
                case "customer":
                    this.customerInformation(this.searchText.value.ID);
                    break;
                case "branch_location":
                    this.branchLocationInformation(this.searchText.value.GroupID);
                    break;
                default:
            }
            this.searchText.setValue("");
            this.filteredSearchItems.set([]);
        }
    }

    searchItemDisplayFn(item: any): string {
        return item && item.DisplayText ? item.DisplayText : "";
    }

    closePanel() {
        if (this.searchRequest) {
            this.searchRequest.unsubscribe();
            this.searchRequest = undefined;
        }
        this.autoSearch.closePanel();
        this.searchText.setValue("", { emitEvent: false });
        this.filteredSearchItems.set([]);
    }

    carrierInformation(carrierId: number) {
        import("../../Shared/carrier-information/carrier-information-dialog/carrier-information-dialog.component").then(
            ({ CarrierInformationDialogComponent }) => {
                this.dialog.open(CarrierInformationDialogComponent, {
                    width: "900px",
                    data: {
                        carrierId: carrierId,
                    },
                });
            }
        );
    }

    customerInformation(customerId) {
        import("../../Shared/customer-information-dialog/customer-information-dialog.component").then(
            ({ CustomerInformationDialogComponent }) => {
                this.dialog.open(CustomerInformationDialogComponent, {
                    width: "900px",
                    data: {
                        customerId: customerId,
                    },
                });
            }
        );
    }

    branchLocationInformation(groupID: number) {
        import("../../Shared/branch-location-information-dialog/branch-location-information-dialog.component").then(
            ({ BranchLocationInformationDialogComponent }) => {
                this.dialog
                    .open(BranchLocationInformationDialogComponent, {
                        width: "900px",
                        data: {
                            branchLocationGroupId: groupID,
                            noEdit: false,
                        },
                    })
                    .afterClosed()
                    .subscribe((result) => (result ? this.editBranchLocation(result) : null));
            }
        );
    }

    editBranchLocation(branchLocation?: IBranchLocation) {
        import("../../Shared/branch-location-editor-dialog/branch-location-editor-dialog.component").then(
            ({ BranchLocationEditorDialogComponent }) => {
                this.dialog
                    .open(BranchLocationEditorDialogComponent, {
                        width: "900px",
                        data: {
                            branchLocation: branchLocation,
                        },
                    })
                    .afterClosed()
                    .subscribe((result) => {
                        if (result as IBranchLocation) {
                            branchLocation = result;
                        }
                    });
            }
        );
    }
}
